import i18next from "i18next";

import moment from "moment";

import { changeLanguageHeader } from "config/axios";
import store from 'config/redux/index';
import { reset } from "config/redux/slice";
import { AppConstants, ServiceConstants } from "constant";
import {
  BankCreditRequestEmploymentResponse,
  BankCreditRequestForm,
  CardResponse,
  CreditRequestChildResponse,
  CreditRequestEmploymentResponse,
  CreditRequestForm,
  CreditRequestIncomeResponse,
  CreditResponse,
  BankCreditHousingResponse,
  Language,
  SwissCanton,
  TabItemProp,
  BankCreditResponse,
} from "types";
import { StorageUtils } from "utils";

const {
  STORAGE_ACCESS_TOKEN,
  STORAGE_VALID_TO,
  STORAGE_USER,
  CREDIT_CONSTANT_C_HIGHER,
  CREDIT_CONSTANT_C_LOWER,
  NATIONALITY_PRIORITY_LIST
} = AppConstants;

const {
  REQUEST_ERROR,
  REQUEST_PENDING,
  REQUEST_SUCCESS
} = ServiceConstants;


export function isEmptySimple(object: any) {
  return (object === undefined || object === null);
}

export function isEmpty(object: any) {
  if (object === undefined || object === null) {
    return true;
  }
  if (typeof object === 'number') {
    return false;
  }
  if (typeof object === 'string') {
    return object.trim().length === 0;
  }
  if (object instanceof Array) {
    return object.length === 0;
  }
  if (object instanceof Date) {
    return !object;
  }
  return Object.keys(object).length === 0;
}

export function convertToAppropriateValue(value: any) {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  if (value === 'true' || value === 'false') {
    return value === 'true';
  }
  if (value instanceof Date) {
    return moment(value).format('YYYY-MM-DD');
  }
  if (value instanceof Array) {
    const newArray: any = new Array(value.length);
    value.forEach((el, index) => {
      newArray[index] = convertToAppropriateValue(el);
    })
    return newArray;
  }
  if (value instanceof Object) {
    const newObject: any = {};
    Object.keys(value).forEach(key => {
      newObject[key] = convertToAppropriateValue(value[key]);
    });
    return newObject;
  }
  return value;
}

export function isEmailValid(email?: string): boolean {
  if (!email) {
    return false;
  }
  const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  return pattern.test(email);
}

export function isCountryCodeValid(code: string) {
  const pattern = /^[0-9]*$/;
  return pattern.test(code);
}

export function isPhoneValid(phone: string) {
  const pattern = /^[0-9]*$/;
  return pattern.test(phone);
}

export function capitalize(text: string | undefined) {
  if (!text) {
    return '';
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function updateTabNumber(tabs: TabItemProp[] | undefined, id: string, number: number) {
  if (!tabs) {
    return undefined;
  }
  tabs.forEach(el => {
    if (el.id === id) el.number = number;
  })
  return tabs;
}

export function logout() {
  StorageUtils.deleteKey(STORAGE_ACCESS_TOKEN);
  StorageUtils.deleteKey(STORAGE_VALID_TO);
  StorageUtils.deleteKey(STORAGE_USER);
}

export function changeLanguage(lang: string) {
  console.log('lang', lang);
  changeLanguageHeader(lang.toLowerCase());
  i18next.changeLanguage(lang.toLowerCase());
}

export function resolveServiceObject(serviceObject: any, serviceId: string, onSuccess: (val: any) => void, onError?: (val: any) => void) {
  if (isEmpty(serviceObject)) {
    return;
  }
  if (serviceObject.status === REQUEST_PENDING) {
    return;
  }
  store.dispatch(reset(serviceId))
  const object = serviceObject;
  if (object.status === REQUEST_SUCCESS) {
    onSuccess(object.data);
  } else if (object.status === REQUEST_ERROR) {
    onError?.(object);
  }
}

export function composeTabTitle(name?: string) {
  if (name) {
    return name;
  }
  return 'Webkredit.ch';
}

export function composeAddress(el: CardResponse | CreditResponse) {
  let composite = '';
  if (el.street) composite += el.street;
  if (el.houseNumber) composite += composite.length > 0 ? (' ' + el.houseNumber) : el.houseNumber;
  if (el.city) composite += composite.length > 0 ? (', ' + el.city) : el.city;
  if (el.postal) composite += composite.length > 0 ? (', ' + el.postal) : el.postal;
  return composite;
}

export function removeObject(form: any, index: number, object: any) {
  const transformed: any = form.getTransformedValues();
  const tempArray: any[] = Object.keys(transformed)
    .map(key => transformed[key])
    .filter((_el, i) => i !== index);
  if (tempArray.length === 0 && object) {
    tempArray.push(object);
  }
  form.setInitialValues(tempArray);
  form.reset();
}

export function addNewObject(form: any, object: any) {
  const tempArray = form.getTransformedValues();
  tempArray[Object.keys(tempArray).length] = object;
  form.setInitialValues(tempArray);
  form.reset();
}

export const getCreditLimit = (creditAmount: number | undefined, paymentPeriod: number | undefined, interestRate: number): number => {
  if (!creditAmount || !paymentPeriod) {
    return 0;
  }
  const c = Math.pow(interestRate + 1, 1 / 12);
  const result = creditAmount * Math.pow(c, paymentPeriod) / (Math.pow(c, paymentPeriod!) - 1) * (c - 1);
  return Math.round(result);
}

export const populateMonthlyInstallment = (creditAmount: number, paymentPeriod: number, form: any) => {
  const minLimit = getCreditLimit(creditAmount, paymentPeriod, CREDIT_CONSTANT_C_LOWER);
  const maxLimit = getCreditLimit(creditAmount, paymentPeriod, CREDIT_CONSTANT_C_HIGHER);

  form.setFieldValue('monthlyInstallmentFrom', minLimit);
  form.setFieldValue('monthlyInstallmentTo', maxLimit);
}

export function getOrderedCountryList(nationalities: string[] | undefined, t: any) {
  if (!nationalities) {
    return [];
  }
  const top: string[] = [];
  const others: string[] = [];

  for (const item of nationalities) {
    if (item !== 'OTHER') {
      if (NATIONALITY_PRIORITY_LIST.includes(item)) {
        top.push(item);
      } else {
        others.push(item);
      }
    }
  }

  const topRenderedArray = top.map(el => ({ value: el, label: t(`user.nationality.options.${el}`) }));
  const otherRenderedArray = others.map(el => ({ value: el, label: t(`user.nationality.options.${el}`) }));
  otherRenderedArray.sort((a, b) => a.label.localeCompare(b.label));
  otherRenderedArray.push({ value: 'OTHER', label: t(`user.nationality.options.OTHER`) })

  return [
    ...topRenderedArray,
    ...otherRenderedArray
  ];
}

export function isYoungerThanTwelveMonths(date: Date): boolean {
  const considerationDate = new Date();
  considerationDate.setMonth(considerationDate.getMonth() - 12);
  return date > considerationDate;
}

export function clearArrayFieldsWhenUpdating(values: any, previous: any, newVisibleFields: string[]) {
  newVisibleFields.forEach(el => {
    if (!newVisibleFields.includes(el) && previous[el] !== undefined) {
      const [, index, fieldName] = el.split('.');
      values[index][fieldName] = undefined;
    }
  });
}

export function updateArrayFieldsWhenVisibilityChanges(prevVisibleFields: string[], newVisibleFields: string[], values: any, form: any) {
  if (prevVisibleFields.length > 0) {
    prevVisibleFields.forEach(el => {
      if (!newVisibleFields.includes(el)) {
        const [, index, fieldName] = el.split('.')
        if (values[index] && values[index][fieldName] !== undefined) {
          values[index][fieldName] = undefined;
          form.setFieldValue(`${index}.${fieldName}`, undefined);
        }
      }
    })
  }
}

const getBaseRequestFields = (values: CreditRequestForm | BankCreditRequestForm) => {
  const array: string[] = [];

  if (values.creditType) {
    array.push('creditPurpose');
    if (values.creditType === 'REFINANCING') {
      array.push('existingMonthlyInstallment');
      array.push('bank');
      if (values.bank === 'OTHER') {
        array.push('bankName');
      }
      array.push('refinanceCreditCard');
      array.push('refinanceLease');
    }
  }

  if (values.creditPurpose === 'OTHER') {
    array.push('creditPurposeNote');
  }

  if (values.saturdayAvailability) {
    array.push('saturdayAvailabilityFrom');
    array.push('saturdayAvailabilityTo');
  }

  if (values.nationality && values.nationality !== 'SWITZERLAND' && values.nationality !== 'LIECHTENSTEIN') {
    array.push('visaType');
    if (values.visaType === 'B') {
      array.push('visaFrom');
    }
  }

  if (values.livingFrom) {
    const considerationDate = new Date();
    considerationDate.setMonth(considerationDate.getMonth() - 12);
    if (new Date(values.livingFrom) > considerationDate) {
      array.push('previousPostal');
      array.push('previousCity');
      array.push('previousStreet');
      array.push('previousHouseNumber');
    }
  }

  return array;
}

const getVisiblePartnerFields = (values: any, isBankCredit: boolean = false) => {
  const array: string[] = [];

  if (values.maritalStatus !== 'MARRIED') {
    return array;
  }

  array.push('partnerOnSameAddress');

  if (isBankCredit) {
    if (values.partnerOnSameAddress === true) {
      array.push('partnerIncludeInApplication');
    }

    if (values.partnerOnSameAddress === false) {
      array.push('partnerResidence');
    }

    if (values.partnerIncludeInApplication) {
      array.push('partnerGender');
      array.push('partnerFirstName');
      array.push('partnerLastName');
      array.push('partnerBirthDate');
      array.push('partnerPhoneNumber');
      array.push('partnerEmail');
      array.push('partnerWorkdayAvailabilityFrom');
      array.push('partnerWorkdayAvailabilityTo');
      array.push('partnerSaturdayAvailability');
      array.push('partnerSaturdayAvailabilityFrom');
      array.push('partnerSaturdayAvailabilityTo');
      array.push('partnerNationality');
      if (values.partnerNationality &&
        values.partnerNationality !== 'SWITZERLAND' &&
        values.partnerNationality !== 'LIECHTENSTEIN') {
        array.push('partnerVisaType');
        if (values.partnerVisaType === 'B') {
          array.push('partnerVisaFrom');
          array.push('partnerVisaTo');
        }
      }
    }

  } else {
    // Regular credit: only show if on same address
    if (values.partnerOnSameAddress) {
      array.push('partnerIncludeInApplication');
      if (values.partnerIncludeInApplication) {
        array.push('partnerFirstName');
        array.push('partnerLastName');
        array.push('partnerGender');
        array.push('partnerBirthDate');
        array.push('partnerEmail');
        array.push('partnerPhoneNumber');
        array.push('partnerNationality');
        array.push('partnerWorkdayAvailabilityFrom');
        array.push('partnerWorkdayAvailabilityTo');
        array.push('partnerSaturdayAvailability');
        if (values.partnerSaturdayAvailability) {
          array.push('partnerSaturdayAvailabilityFrom');
          array.push('partnerSaturdayAvailabilityTo');
        }
        if (values.partnerNationality && values.partnerNationality !== 'SWITZERLAND' && values.partnerNationality !== 'LIECHTENSTEIN') {
          array.push('partnerVisaType');
          if (values.partnerVisaType === 'B') {
            array.push('partnerVisaFrom');
          }
        }
      }
    }
  }

  return array;
}

export const getVisibleCreditRequestFields = (values: CreditRequestForm) => {
  const array = getBaseRequestFields(values);

  if (values.rent === true) {
    array.push('rentAmount');
  }
  if (values.mortgage === true) {
    array.push('mortgageAmount');
  }

  array.push(...getVisiblePartnerFields(values, false));

  return array;
}

export function getVisibleChildrenFields(values: any) {
  const tempArray: string[] = [];
  if (values) {
    let array: CreditRequestChildResponse[] = [];
    if (!(values instanceof Array)) {
      Object.keys(values).forEach(key => array.push(values[key]));
    } else {
      array = values;
    }
    Object.keys(values).forEach(key => {
      const item: CreditRequestChildResponse = values[key as any];
      if (item.craft) {
        tempArray.push(`children.${key}.craftAmount`)
      }
    })
  }
  return tempArray
}

export function getVisibleHousingCostSharingFields(values: BankCreditHousingResponse[] | undefined) {
  const tempArray: string[] = [];
  if (values) {
    Object.keys(values).forEach(key => {
      const item = values[key as any];
      if (item.costSharingWith === 'OTHER') {
        tempArray.push(`housingExpenses.${key}.costSharingPerson`)
      }
    })
  }
  return tempArray
}

export function getVisibleEmploymentFields(
  values: any,
  isBankCredit: boolean = false,
  creditData?: BankCreditResponse,
) {
  const tempArray: string[] = [];
  if (values) {
    let array: (CreditRequestEmploymentResponse | BankCreditRequestEmploymentResponse)[] = [];
    if (!(values instanceof Array)) {
      Object.keys(values).forEach(key => array.push(values[key]));
    } else {
      array = values;
    }
    array.forEach((el, index) => {
      if (el.professionalSituation) {
        if (el.professionalSituation === 'EMPLOYED' || el.professionalSituation === 'AGENCY') {
          if (isBankCredit) {
            tempArray.push(`employments.${index}.employerName`);
            tempArray.push(`employments.${index}.employerStreet`);
            tempArray.push(`employments.${index}.employerHouseNumber`);
            tempArray.push(`employments.${index}.employerPostal`);
            tempArray.push(`employments.${index}.employerCity`);
            tempArray.push(`employments.${index}.employerPhoneNumber`);
            tempArray.push(`employments.${index}.sickLeaveOnPaylist`);
            tempArray.push(`employments.${index}.travelExpensesBn`);
            tempArray.push(`employments.${index}.travelExpensesCb`);
            if (
              ('travelingToWorkBn' in el && el.travelingToWorkBn === 'CAR') ||
              ('travelingToWorkCb' in el && el.travelingToWorkCb === 'CAR')
            ) {
              tempArray.push(`employments.${index}.parkingOnPaylist`);
            }
            if (creditData?.supportsChildren) {
              tempArray.push(`employments.${index}.childAllowanceForOlderChildren`);
              tempArray.push(`employments.${index}.childAllowanceForYoungerChildren`);
              tempArray.push(`employments.${index}.childAllowanceMatches`);
            }
          } else {
            tempArray.push(`employments.${index}.travelingToWork`);
          }
          if (el.professionalSituation === 'EMPLOYED') {
            tempArray.push(`employments.${index}.thirteenthSalary`);
            tempArray.push(`employments.${index}.bonusReceived`);
            if (isBankCredit) {
              tempArray.push(`employments.${index}.salaryStructure`);
            }
          }
          if (el.professionalSituation === 'AGENCY') {
            tempArray.push(`employments.${index}.employmentType`);
          }
          tempArray.push(`employments.${index}.contractStartDate`);
          if (el.employmentType === 'DEFINED') {
            tempArray.push(`employments.${index}.contractEndDate`);
          }
        } else if (el.professionalSituation === 'SELF_EMPLOYED_AG') {
          tempArray.push(`employments.${index}.requesterPaysSalary`);
        } else if (el.professionalSituation === 'PENSION' || el.professionalSituation === 'DISABILITY_PENSION') {
          tempArray.push(`employments.${index}.socialHelp`);
        }
      }

      if (el.contractStartDate) {
        const monthsDiff = moment().diff(moment(el.contractStartDate), 'months');
        if (monthsDiff < 3) {
          if (isBankCredit) {
            tempArray.push(`employments.${index}.previousEmployerName`);
            tempArray.push(`employments.${index}.previousEmployerStartDate`);
          }
        }
      }

      if (isBankCredit && 'employerLoanAmount' in el && el.employerLoanAmount && el.employerLoanAmount > 0) {
        tempArray.push(`employments.${index}.employerLoanPaidOut`);
      }

      // Show iban when ibanOnPaylist is true
      if (isBankCredit && 'ibanOnPaylist' in el && el.ibanOnPaylist === true) {
        tempArray.push(`employments.${index}.iban`);
      }

      if (isBankCredit && el.bonusReceived === true) {
        tempArray.push(`employments.${index}.bonusAmount`);
        tempArray.push(`employments.${index}.bonusType`);
      }

      if (isBankCredit && creditData) {
        let shouldShowField = false;

        const applicantHasBVisa = creditData.visaType === "B";
        const partnerIncluded = creditData.partnerIncludeInApplication === true;
        const partnerHasBVisa = creditData.partnerVisaType === "B";

        if (!partnerIncluded) {
          shouldShowField = applicantHasBVisa;
        } else {
          shouldShowField = applicantHasBVisa && partnerHasBVisa;
        }

        if (shouldShowField) {
          tempArray.push(`employments.${index}.quellensteuerMatchesTariff`);
        }
      }
    });
  }
  return tempArray;
}

export function getVisibleIncomeFields(values: any) {
  const tempArray: string[] = [];
  if (values) {
    let array: CreditRequestIncomeResponse[] = [];
    if (!(values instanceof Array)) {
      Object.keys(values).forEach(key => array.push(values[key]));
    } else {
      array = values;
    }
    Object.keys(values).forEach(key => {
      const item: CreditRequestIncomeResponse = values[key as any];
      if (item.incomeType === 'RENT') {
        tempArray.push(`incomes.${key}.startDate`)
      }
    })
  }
  return tempArray
}

export const getVisibleBankRequestFields = (values: BankCreditRequestForm) => {
  const array = getBaseRequestFields(values);

  if (values.creditType === 'REFINANCING') {
    array.push('existingCreditAmount');

    if (values.refinanceLease) {
      array.push('existingLeaseAmount');
      array.push('existingLeaseMonthlyInstallment');
      array.push('existingLeaseResidualValue');
    }
  }

  if (values.bank === 'BANK_NOW') {
    array.push('productBn');
  }

  if (array.includes('previousPostal')) {
    array.push('previousCanton');
  }

  if (values.nationality && values.nationality !== 'SWITZERLAND' && values.nationality !== 'LIECHTENSTEIN') {
    if (values.visaType === 'B') {
      array.push('visaTo');
    }
  }

  if (values.supportsChildren) {
    array.push('childrenLiveWithYou');

    if (values.childrenLiveWithYou === 'NO' || values.childrenLiveWithYou === 'PARTIALLY') {
      array.push('childrenInAbroad');
      array.push('childrenCaretaker');

      if (values.childrenInAbroad === true) {
        array.push('foreignTransferAmount');
      } else if (values.childrenInAbroad === false) {
        array.push('paysAlimony');
        if (values.paysAlimony) {
          array.push('monthlyAlimonyExpensesBn');
          array.push('monthlyAlimonyExpensesCb');
          array.push('monthlyAlimonyExpensesCc');
        }
      }

      if (values.childrenCaretaker === 'THIRD_PERSON') {
        array.push('childrenCaretakerFullName');
      }
    }
  }

  array.push(...getVisiblePartnerFields(values, true));

  if (values.rent === true) {
    array.push('additionalHousingExpensesIncluded')
    array.push('rentAmountBn');
    array.push('rentAmountCb');
    array.push('rentAmountCc');
  }

  if (values.mortgage === true) {
    array.push('mortgageAmount');
    array.push('residenceSameAsMortgageAddress');
    array.push('mortgagePaidOff');
    array.push('mortgageOwnedSince');
    array.push('includeAmortization');

    if (values.mortgagePaidOff === true) {
      array.push('finalTaxCalculation');
    }

    if (values.includeAmortization === true) {
      array.push('amortizationAmount');
      array.push('amortizationType');
    }

    if (values.residenceSameAsMortgageAddress === false) {
      array.push('mortgagePostal');
      array.push('mortgageCity');
      array.push('mortgageStreet');
      array.push('mortgageHouseNumber');
      array.push('mortgageCanton');
    }
  }

  if (values.houseSituation && values.houseSituation !== 'ALONE') {
    array.push('housingCostSplitCount');
  }

  return array;
}

// Map of Swiss cantons to their official language
const CANTON_LANGUAGE_MAP: Record<string, Language> = {
  AG: 'DE',
  AI: 'DE',
  AR: 'DE',
  BE: 'DE', // Bilingual (German/French), but primarily German
  BL: 'DE',
  BS: 'DE',
  FR: 'FR', // Bilingual (French/German), but primarily French
  GE: 'FR',
  GL: 'DE',
  GR: 'DE', // Trilingual (German/Romansh/Italian), but primarily German
  JU: 'FR',
  LU: 'DE',
  NE: 'FR',
  NW: 'DE',
  OW: 'DE',
  SG: 'DE',
  SH: 'DE',
  SO: 'DE',
  SZ: 'DE',
  TG: 'DE',
  TI: 'IT',
  UR: 'DE',
  VD: 'FR',
  VS: 'FR', // Bilingual (French/German), but primarily French
  ZG: 'DE',
  ZH: 'DE',
};

const NATIONALITY_LANGUAGE_MAP: Record<string, Language> = {
  // German-speaking countries
  GERMANY: 'DE',
  AUSTRIA: 'DE',
  SWITZERLAND: 'DE', // Default to German for Switzerland
  LIECHTENSTEIN: 'DE',
  LUXEMBOURG: 'DE', // Trilingual, but German is common
  BELGIUM: 'DE', // Trilingual, using German as one option

  // French-speaking countries
  FRANCE: 'FR',
  MONACO: 'FR',

  // Italian-speaking countries
  ITALY: 'IT',
  SAN_MARINO: 'IT',
  VATICAN_CITY: 'IT',
};

/**
 * Determines the credit language based on nationality and canton
 * @param nationality - The nationality of the person
 * @param canton - The Swiss canton (two-letter code)
 * @returns The credit language, defaults to DE (German) if no match
 */
export function getCreditLanguage(nationality?: string, canton?: SwissCanton): Language {
  if (nationality && NATIONALITY_LANGUAGE_MAP[nationality]) {
    return NATIONALITY_LANGUAGE_MAP[nationality];
  }

  if (canton && CANTON_LANGUAGE_MAP[canton.value]) {
    return CANTON_LANGUAGE_MAP[canton.value];
  }

  return 'DE';
}

type TariffPrefix = "A" | "C" | "H";
type TariffCode = `${TariffPrefix}${0 | 1 | 2 | 3 | 4 | 5}N`;

interface TariffInput {
  partnerIncluded: boolean;   // true = married & partner employed
  childrenCount: number;      // max supported children 5
}

export function calculateQuellensteuerTariffCode(input: TariffInput): TariffCode {
  const { partnerIncluded, childrenCount } = input;

  const childCountSupported = Math.min(Math.max(childrenCount, 0), 5) as 0 | 1 | 2 | 3 | 4 | 5;
  const prefix: TariffPrefix = partnerIncluded ? "C" : childCountSupported > 0 ? "H" : "A";

  return `${prefix}${childCountSupported}N`;
}
